import React, { useState } from 'react';
import { AlertTriangle, FileText, Phone, Check, Minus, Plus } from 'lucide-react';
const COLORS = [{
  name: 'Dark Bronze',
  hex: '#4A4138'
}, {
  name: 'Black',
  hex: '#222222'
}, {
  name: 'Gold Speck',
  hex: '#C5A059'
}, {
  name: 'Postal Grey',
  hex: '#8A8D8F'
}, {
  name: 'Sandstone',
  hex: '#D2C4A7'
}, {
  name: 'Silver Speck',
  hex: '#B4B7BA'
}, {
  name: 'White',
  hex: '#F8F9FA'
}, {
  name: 'Antique Bronze',
  hex: '#5C4E3A'
}];
const LIGHT_COLORS = ['White', 'Sandstone', 'Silver Speck', 'Gold Speck'];
const ID_OPTIONS = ['Standard Decal ID', 'Non Standard Decal ID', 'Engraved - No Fill ID'];
interface ProductInfoProps {
  onOptionsChange: (options: any) => void;
}
export function ProductInfo({
  onOptionsChange
}: ProductInfoProps) {
  const [selectedColor, setSelectedColor] = useState(COLORS[0].name);
  const [selectedId, setSelectedId] = useState(ID_OPTIONS[0]);
  const [sequence, setSequence] = useState('');
  const [reviewRequested, setReviewRequested] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const emit = (next: Partial<Record<string, any>>) => {
    onOptionsChange({
      selectedColor,
      selectedId,
      sequence,
      reviewRequested,
      quantity,
      ...next
    });
  };
  const updateColor = (value: string) => {
    setSelectedColor(value);
    emit({
      selectedColor: value
    });
  };
  const updateId = (value: string) => {
    setSelectedId(value);
    emit({
      selectedId: value
    });
  };
  const updateSequence = (value: string) => {
    setSequence(value);
    emit({
      sequence: value
    });
  };
  const updateReview = (value: boolean) => {
    setReviewRequested(value);
    emit({
      reviewRequested: value
    });
  };
  const updateQuantity = (value: number) => {
    const q = Math.max(1, value);
    setQuantity(q);
    emit({
      quantity: q
    });
  };
  return <div className="flex flex-col" data-id="element-693" data-mp="3ui8ndwt83x92">
      {/* Header Info */}
      <div className="mb-6" data-id="element-694" data-mp="3uhywm70ctm2b">
        <span className="text-sm font-bold text-brand-green uppercase tracking-wider mb-2 block" data-id="element-695" data-mp="3bxtckmmrwr8">
          Commercial Mailboxes
        </span>
        <h1 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-2" data-id="element-696" data-mp="83pay5nt6s58">
          Versatile 4C Depot 16H - 20 Door Package
        </h1>
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4" data-id="element-697" data-mp="3bje9sr5hx2uh">
          <span data-id="element-698" data-mp="3blh9k2wgaace">
            SKU:{' '}
            <span className="font-mono text-brand-charcoal" data-id="element-699" data-mp="3ghxuypc9mals">4C16D-20-D</span>
          </span>
        </div>
        <div className="text-3xl font-bold text-brand-green" data-id="element-700" data-mp="3lhaua9kbpa59">$4,353.00</div>
      </div>

      {/* Compliance Notice (CRITICAL) */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start gap-3" data-id="element-701" data-mp="3qi8bojxie05p">
        <AlertTriangle className="text-brand-red shrink-0 mt-0.5" size={20} data-id="element-702" data-mp="10qtjgkm41olr" />
        <div data-id="element-703" data-mp="15pd84ss39eht">
          <h4 className="font-bold text-brand-red text-sm mb-1" data-id="element-704" data-mp="15p3ix7evbbd6">
            Important Compliance Notice
          </h4>
          <p className="text-sm text-red-900 leading-snug" data-id="element-705" data-mp="2q7nt8qx59em5">
            The Depot is a freestanding solution that provides flexibility to
            install STD-4C wall-mounted mailboxes in a standalone cabinet. This
            installation option is{' '}
            <strong data-id="element-706" data-mp="2bkcbu8ea70e4">not approved for USPS Delivery</strong>. Confirm delivery
            use and project requirements before ordering.
          </p>
        </div>
      </div>

      {/* PDF Links (pending integration) */}
      <div className="flex flex-wrap gap-4 mb-8 border-b border-brand-border pb-8" data-id="element-707" data-mp="22i323kqu7b1s">
        {/* TODO: wire to verified Website Forge product PDF assets */}
        <span className="flex items-center gap-2 text-sm font-medium text-gray-400 cursor-not-allowed" aria-disabled="true" title="PDF link pending Website Forge integration" data-id="element-708" data-mp="1skyh3o8q9lmk">
          <FileText size={16} data-id="element-709" data-mp="3myg9bok2v6nx" /> Product PDF — pending integration
        </span>
        <span className="flex items-center gap-2 text-sm font-medium text-gray-400 cursor-not-allowed" aria-disabled="true" title="PDF link pending Website Forge integration" data-id="element-710" data-mp="2kll2xksf009o">
          <FileText size={16} data-id="element-711" data-mp="2anx70rj2ompx" /> PDF Line Drawings — pending integration
        </span>
      </div>

      {/* Options UI */}
      <div className="space-y-8" id="configure-options" data-id="element-712" data-mp="2fkxacuojfbdc">
        {/* Colors */}
        <div data-id="element-713" data-mp="20x2mbrlh5v6l">
          <div className="flex justify-between items-end mb-3" data-id="element-714" data-mp="20wj1o34fwsov">
            <h3 className="font-bold text-brand-charcoal" data-id="element-715" data-mp="25okkk7w45hnv">1. Select Color</h3>
            <span className="text-sm text-gray-500" data-id="element-716" data-mp="25pvl7yu9i9ar">
              Selected:{' '}
              <span className="font-semibold text-brand-charcoal" data-id="element-717" data-mp="1rujj36ds1gjt">
                {selectedColor}
              </span>
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3" data-id="element-718" data-mp="1hy6q6aq236q4">
            {COLORS.map(color => {
            const isSelected = selectedColor === color.name;
            return <button key={color.name} type="button" onClick={() => updateColor(color.name)} aria-label={`Color: ${color.name}`} aria-pressed={isSelected} className={`flex flex-col items-center gap-2 p-2 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-brand-maroon ${isSelected ? 'border-brand-maroon bg-brand-maroon/5' : 'border-brand-border hover:border-brand-maroon/40'}`} data-id="element-719" data-mp="13bhpw6nzcpio">
                  <span className="w-9 h-9 rounded-full border border-black/10 shadow-sm flex items-center justify-center" style={{
                backgroundColor: color.hex
              }} data-id="element-720" data-mp="1i88ucucigrf5">
                    {isSelected && <Check size={15} color={LIGHT_COLORS.includes(color.name) ? '#000' : '#FFF'} data-id="element-721" data-mp="25zea79igbjpg" />}
                  </span>
                  <span className="text-[11px] leading-tight text-center font-medium text-brand-charcoal" data-id="element-722" data-mp="2d19q01rwiy8a">
                    {color.name}
                  </span>
                </button>;
          })}
          </div>
        </div>

        {/* Door ID */}
        <div data-id="element-723" data-mp="2mtfhlae8rdmy">
          <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-724" data-mp="2rqz40xl3ewpz">
            2. Door Identification
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-725" data-mp="2vrrcvpq1yzlq">
            {ID_OPTIONS.map(opt => <button key={opt} type="button" onClick={() => updateId(opt)} aria-pressed={selectedId === opt} className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all text-left focus:outline-none focus:ring-2 focus:ring-brand-maroon ${selectedId === opt ? 'border-brand-maroon bg-brand-maroon/5 text-brand-maroon' : 'border-brand-border bg-white text-brand-charcoal hover:border-brand-maroon/50'}`} data-id="element-726" data-mp="30r422h4ecirh">
                {opt}
              </button>)}
          </div>
        </div>

        {/* Door Sequence */}
        <div data-id="element-727" data-mp="u3msr3on6k37">
          <h3 className="font-bold text-brand-charcoal mb-1" data-id="element-728" data-mp="u10p3f4vgoq9">
            3. Door Sequence
          </h3>
          <p className="text-xs text-gray-500 mb-3" data-id="element-729" data-mp="p1xl7y0moojv">
            Standard number will be sequential if none entered below.
          </p>
          <textarea value={sequence} onChange={e => updateSequence(e.target.value)} placeholder="Enter custom sequence or project notes here..." aria-label="Door sequence or project notes" className="w-full p-3 rounded-lg border border-brand-border focus:ring-2 focus:ring-brand-maroon focus:border-brand-maroon text-sm min-h-[80px]" data-id="element-730" data-mp="1hs663xmjtn25" />
        </div>

        {/* Quantity */}
        <div data-id="element-731" data-mp="3sjfpxwl3tfg2">
          <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-732" data-mp="3sn3jgvqju1us">4. Quantity</h3>
          <div className="inline-flex items-center border border-brand-border rounded-lg overflow-hidden" data-id="element-733" data-mp="m1xf86jw2bwr">
            <button type="button" onClick={() => updateQuantity(quantity - 1)} aria-label="Decrease quantity" className="px-3 py-2.5 text-brand-charcoal hover:bg-brand-cream transition-colors" data-id="element-734" data-mp="r3wdj20ruimu">
              <Minus size={16} data-id="element-735" data-mp="14v183ppa8kqb" />
            </button>
            <input type="number" min={1} value={quantity} onChange={e => updateQuantity(parseInt(e.target.value || '1', 10))} aria-label="Quantity" className="w-16 text-center border-x border-brand-border py-2.5 focus:outline-none focus:ring-1 focus:ring-brand-maroon font-semibold" data-id="element-736" data-mp="14wcdtv2ag2k2" />
            <button type="button" onClick={() => updateQuantity(quantity + 1)} aria-label="Increase quantity" className="px-3 py-2.5 text-brand-charcoal hover:bg-brand-cream transition-colors" data-id="element-737" data-mp="1sjc3weg4fu7q">
              <Plus size={16} data-id="element-738" data-mp="38amrb548h88i" />
            </button>
          </div>
        </div>

        {/* Review Checkbox */}
        <div className="bg-brand-goldSoft p-4 rounded-lg border border-brand-gold/30" data-id="element-739" data-mp="3n1n940qveehb">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-740" data-mp="3i2ss94cgqzzu">
            <input type="checkbox" checked={reviewRequested} onChange={e => updateReview(e.target.checked)} className="mt-1 w-4 h-4 text-brand-maroon border-gray-300 rounded focus:ring-brand-maroon" data-id="element-741" data-mp="2ubws9jxusyhi" />
            <div data-id="element-742" data-mp="2z9ow9cbrwb22">
              <span className="font-bold text-brand-charcoal block text-sm" data-id="element-743" data-mp="2z96phlrhiefw">
                I want MidAtlantic to review this before ordering.
              </span>
              <span className="text-xs text-gray-600" data-id="element-744" data-mp="ejzeorjoqb5o">
                Recommended for complex commercial projects to confirm delivery
                use, compatibility, numbering, and project requirements before
                ordering.
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* Mobile CTAs */}
      <div className="mt-8 flex flex-col gap-3 lg:hidden" data-id="element-745" data-mp="xjn4ojhwfafl">
        <a href="#configure-options" className="bg-brand-maroon text-white text-center py-3 rounded-md font-bold" data-id="element-746" data-mp="sm4rui0rgb2b">
          Configure options
        </a>
        <a href="/ContactUs.html" className="bg-white border border-brand-border text-brand-charcoal text-center py-3 rounded-md font-bold" data-id="element-747" data-mp="jl6o80u8i5k9">
          Ask before ordering
        </a>
        <a href="tel:301-620-7585" className="text-brand-charcoal hover:text-brand-maroon text-center py-3 font-bold flex items-center justify-center gap-2" data-id="element-748" data-mp="9n92bjzukkut">
          <Phone size={18} data-id="element-749" data-mp="2410iz3glg4zr" /> Call 301-620-7585
        </a>
      </div>
    </div>;
}